/**
 * Tipi e costanti per l'applicazione turni medici
 */

import categoryToUocJson from '../shared/category_to_uoc.json';

// Macro-aree di competenza UOC/UOS
export const MACRO_AREAS = {
  TERAPIA_INTENSIVA: 'terapia_intensiva',
  SALA_OPERATORIA: 'sala_operatoria',
  MEDICINA_DOLORE: 'medicina_dolore',
  DIREZIONE: 'direzione',
};

export const MACRO_AREA_LABELS = {
  [MACRO_AREAS.TERAPIA_INTENSIVA]: 'Terapia Intensiva',
  [MACRO_AREAS.SALA_OPERATORIA]: 'Sala Operatoria / NORA',
  [MACRO_AREAS.MEDICINA_DOLORE]: 'Medicina del dolore / Cure palliative',
  [MACRO_AREAS.DIREZIONE]: 'Direzione UO',
};

/** Sezioni PDF / ordinamento: raggruppano più categorie slot */
export const PDF_SECTION_ORDER = [
  MACRO_AREAS.TERAPIA_INTENSIVA,
  MACRO_AREAS.SALA_OPERATORIA,
  MACRO_AREAS.MEDICINA_DOLORE,
  MACRO_AREAS.DIREZIONE,
];

export const PDF_SECTION_LABELS = {
  [MACRO_AREAS.TERAPIA_INTENSIVA]: 'Terapia intensiva e formazione',
  [MACRO_AREAS.SALA_OPERATORIA]: 'Sala operatoria e NORA',
  [MACRO_AREAS.MEDICINA_DOLORE]: 'Medicina del dolore e cure palliative',
  [MACRO_AREAS.DIREZIONE]: 'Direzione UO',
};

// Categorie slot turno
export const SLOT_CATEGORIES = {
  TERAPIA_INTENSIVA: 'terapia_intensiva',
  SALA_OPERATORIA: 'sala_operatoria',
  NORA: 'nora',
  MEDICINA_DOLORE: 'medicina_dolore',
  CURE_PALLIATIVE: 'cure_palliative',
  FORMAZIONE: 'formazione',
  DIREZIONE: 'direzione',
};

export const SLOT_CATEGORY_LABELS = {
  [SLOT_CATEGORIES.TERAPIA_INTENSIVA]: 'Terapia Intensiva',
  [SLOT_CATEGORIES.SALA_OPERATORIA]: 'Sala operatoria',
  [SLOT_CATEGORIES.NORA]: 'NORA',
  [SLOT_CATEGORIES.MEDICINA_DOLORE]: 'Medicina del dolore',
  [SLOT_CATEGORIES.CURE_PALLIATIVE]: 'Cure palliative',
  [SLOT_CATEGORIES.FORMAZIONE]: 'Formazione (turno)',
  [SLOT_CATEGORIES.DIREZIONE]: 'Direzione',
};

/** Chiavi JSON `doctor.distribuzione` (5 assi). SO e NORA condividono `sala_operatoria`. */
export const DISTRIBUTION_KEYS = {
  TERAPIA_INTENSIVA: 'terapia_intensiva',
  FORMAZIONE: 'formazione',
  SALA_OPERATORIA: 'sala_operatoria',
  MEDICINA_DOLORE: 'medicina_dolore',
  CURE_PALLIATIVE: 'cure_palliative',
};

export const DISTRIBUTION_AXIS_ORDER = [
  DISTRIBUTION_KEYS.TERAPIA_INTENSIVA,
  DISTRIBUTION_KEYS.FORMAZIONE,
  DISTRIBUTION_KEYS.SALA_OPERATORIA,
  DISTRIBUTION_KEYS.MEDICINA_DOLORE,
  DISTRIBUTION_KEYS.CURE_PALLIATIVE,
];

export const DISTRIBUTION_AXIS_LABELS = {
  [DISTRIBUTION_KEYS.TERAPIA_INTENSIVA]: 'Terapia intensiva (feriali diurni)',
  [DISTRIBUTION_KEYS.FORMAZIONE]: 'Formazione',
  [DISTRIBUTION_KEYS.SALA_OPERATORIA]: 'Sala operatoria / NORA',
  [DISTRIBUTION_KEYS.MEDICINA_DOLORE]: 'Medicina del dolore',
  [DISTRIBUTION_KEYS.CURE_PALLIATIVE]: 'Cure palliative',
};

/**
 * Sala operatoria di Medicina del dolore: nome standard "SO MedDol", "SO MedDol2"…
 * (anche compatto SOMedDol2). Categoria effettiva MD / UOS MD — non slot SO generici.
 */
export function isSoMedicinaDoloreSlot(slot) {
  if (!slot) return false;
  const raw = String(slot.name || '').trim();
  if (!raw) return false;
  // "SO MedDol" → someddol (due "d"); si accetta anche la forma rara somedol.
  const collapsed = raw.replace(/[\s_-]+/g, '').toLowerCase();
  return /^somedd?ol\d*$/i.test(collapsed);
}

/** Categoria slot → chiave distribuzione (null = nessuna barra, es. direzione). */
export function slotCategoryToDistKey(category, slot = null) {
  if (slot && isSoMedicinaDoloreSlot(slot)) return DISTRIBUTION_KEYS.MEDICINA_DOLORE;
  if (!category) return null;
  if (category === 'direzione') return null;
  if (category === 'nora') return DISTRIBUTION_KEYS.SALA_OPERATORIA;
  if (DISTRIBUTION_AXIS_ORDER.includes(category)) return category;
  return category;
}

/**
 * Chiave ripartizione per il popup manuale "0% nel profilo" (barre TI/SO/MD/CP…).
 * Slot Direzione: nessuna barra → null (competenza Direzione = solo flag Abilità Direzione).
 */
export function zeroPercentWarningDistKey(slot) {
  const k = slotCategoryToDistKey(slot?.category, slot);
  if (k != null) return k;
  if (slot && String(slot.uocId || '') === 'uos_medicina_dolore') return DISTRIBUTION_KEYS.MEDICINA_DOLORE;
  return null;
}

/** Etichetta area per il messaggio del popup 0% (assi di ripartizione). */
export function zeroPercentWarningAreaLabel(slot, distKey) {
  return DISTRIBUTION_AXIS_LABELS[distKey] || SLOT_CATEGORY_LABELS[slot?.category] || distKey || 'questa area';
}

/** Macro sezione PDF / intestazione tabella */
export function slotCategoryToPdfSection(category, slot = null) {
  if (slot && isSoMedicinaDoloreSlot(slot)) return MACRO_AREAS.MEDICINA_DOLORE;
  if (!category) return MACRO_AREAS.DIREZIONE;
  if (category === 'formazione' || category === 'terapia_intensiva') return MACRO_AREAS.TERAPIA_INTENSIVA;
  if (category === 'nora' || category === 'sala_operatoria') return MACRO_AREAS.SALA_OPERATORIA;
  if (category === 'medicina_dolore' || category === 'cure_palliative') return MACRO_AREAS.MEDICINA_DOLORE;
  if (category === 'direzione') return MACRO_AREAS.DIREZIONE;
  return MACRO_AREAS.DIREZIONE;
}

const WEEKDAY_FERIALI = ['lun', 'mar', 'mer', 'gio', 'ven'];
const TIME_SLOTS_DIURNI_FERIALI = ['mattino', 'pomeriggio', 'prolungato'];

/** Barra TI (distribuzione) si applica a questi slot TI */
export function isTerapiaIntensivaFerialeDiurnoSlot(slot) {
  if (!slot || slot.category !== 'terapia_intensiva') return false;
  return WEEKDAY_FERIALI.includes(slot.weekday) && TIME_SLOTS_DIURNI_FERIALI.includes(slot.timeSlot);
}

// Tipo attività (clinico / non clinico / direzione)
export const ACTIVITY_TYPES = {
  CLINICO: 'clinico',
  NON_CLINICO: 'non_clinico',
  DIREZIONE: 'direzione',
};

export const ACTIVITY_TYPE_LABELS = {
  [ACTIVITY_TYPES.CLINICO]: 'Clinico',
  [ACTIVITY_TYPES.NON_CLINICO]: 'Non clinico (ambulatoriale)',
  [ACTIVITY_TYPES.DIREZIONE]: 'Direzione',
  reperibilita: 'Reperibilità',
};

// Fascie orarie
export const TIME_SLOTS = {
  MATTINO: 'mattino',      // 8-14
  POMERIGGIO: 'pomeriggio', // 14-20
  PROLUNGATO: 'prolungato', // 8-15:30, 7,5h
  NOTTE: 'notte',          // 20-8
  REPERIBILITA_NOTTE: 'reperibilita_notte', // 20-8, tutte le notti
  REPERIBILITA_GIORNO: 'reperibilita_giorno', // 8-20, sab e dom
};

export const TIME_SLOT_LABELS = {
  [TIME_SLOTS.MATTINO]: 'Mattino (8-14)',
  [TIME_SLOTS.POMERIGGIO]: 'Pomeriggio (14-20)',
  [TIME_SLOTS.PROLUNGATO]: 'Prolungato (8-15:30)',
  [TIME_SLOTS.NOTTE]: 'Notte (20-8)',
  [TIME_SLOTS.REPERIBILITA_NOTTE]: 'Reperibilità notte (20-8)',
  [TIME_SLOTS.REPERIBILITA_GIORNO]: 'Reperibilità giorno (8-20)',
};

/** Ordine fasce per calendario settimanale (reperibilità giorno sopra notte) */
export const TIME_SLOT_ORDER_CALENDAR = [
  TIME_SLOTS.MATTINO,
  TIME_SLOTS.POMERIGGIO,
  TIME_SLOTS.PROLUNGATO,
  TIME_SLOTS.NOTTE,
  TIME_SLOTS.REPERIBILITA_GIORNO,
  TIME_SLOTS.REPERIBILITA_NOTTE,
];

// Attività speciali
export const ACTIVITY_TYPES_EXT = {
  ...ACTIVITY_TYPES,
  REPERIBILITA: 'reperibilita',
};

// Categorie assenza
export const ABSENCE_CATEGORIES = {
  CONGEDO_ORDINARIO: 'congedo_ordinario',
  /** Congedo residuo anno precedente: stesso trattamento operativo del CO, ma non conta nel tetto 26 gg del riepilogo annuale */
  CONGEDO_ORDINARIO_ANNO_PRECEDENTE: 'congedo_ordinario_anno_precedente',
  RISCHIO_RADIOLOGICO: 'rischio_radiologico',
  PERMESSO: 'permesso',
  FORMAZIONE: 'formazione',
  CONGEDO_MALATTIA: 'congedo_malattia',
  LIBERA_PROFESSIONE: 'libera_professione',
};

export const ABSENCE_CATEGORY_LABELS = {
  [ABSENCE_CATEGORIES.CONGEDO_ORDINARIO]: 'Congedo Ordinario',
  [ABSENCE_CATEGORIES.CONGEDO_ORDINARIO_ANNO_PRECEDENTE]: 'Congedo Ordinario (anno precedente)',
  [ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO]: 'Rischio Radiologico',
  [ABSENCE_CATEGORIES.PERMESSO]: 'Permesso',
  [ABSENCE_CATEGORIES.FORMAZIONE]: 'Formazione',
  [ABSENCE_CATEGORIES.CONGEDO_MALATTIA]: 'Congedo Malattia/Maternità',
  [ABSENCE_CATEGORIES.LIBERA_PROFESSIONE]: 'Libera Professione',
};

/** Abbreviazioni per le celle del calendario assenze */
export const ABSENCE_CATEGORY_ABBR = {
  [ABSENCE_CATEGORIES.CONGEDO_ORDINARIO]: 'CO',
  [ABSENCE_CATEGORIES.CONGEDO_ORDINARIO_ANNO_PRECEDENTE]: 'CO (AP)',
  [ABSENCE_CATEGORIES.RISCHIO_RADIOLOGICO]: 'Rx',
  [ABSENCE_CATEGORIES.PERMESSO]: 'PS',
  [ABSENCE_CATEGORIES.FORMAZIONE]: 'F',
  [ABSENCE_CATEGORIES.CONGEDO_MALATTIA]: 'M',
  [ABSENCE_CATEGORIES.LIBERA_PROFESSIONE]: 'LP',
};

// Slot per assenze Libera Professione e Formazione (esclusione selettiva)
export const SLOT_ABSENCE_TYPES = ['mattino', 'pomeriggio', 'prolungato', 'notte'];
export const SLOT_ABSENCE_LABELS = { mattino: 'Mattino', pomeriggio: 'Pomeriggio', prolungato: 'Prolungato', notte: 'Notte' };

// Ore formazione creditate al monte orario (7.6 = giornata intera)
export const ORE_FORMAZIONE_OPTIONS = [1, 2, 3, 4, 5, 6, 7.6];
export const ORE_FORMAZIONE_LABELS = { 1: '1h', 2: '2h', 3: '3h', 4: '4h', 5: '5h', 6: '6h', 7.6: 'Giornata (7.6h)' };

// Desiderata - esclusioni da slot specifici
export const DESIDERATA_TYPES = {
  NO_MATTINO: 'no_mattino',
  NO_POMERIGGIO: 'no_pomeriggio',
  NO_POMERIGGIO_NOTTE: 'no_pomeriggio_notte', // retrocompat: non mostrato in UI
  NO_NOTTE: 'no_notte',
  NO_REPERIBILITA_GIORNO: 'no_reperibilita_giorno',
  NO_REPERIBILITA_NOTTE: 'no_reperibilita_notte',
};

export const DESIDERATA_LABELS = {
  [DESIDERATA_TYPES.NO_MATTINO]: 'No Mattino',
  [DESIDERATA_TYPES.NO_POMERIGGIO]: 'No Pomeriggio',
  [DESIDERATA_TYPES.NO_POMERIGGIO_NOTTE]: 'No Pomeriggio-Notte',
  [DESIDERATA_TYPES.NO_NOTTE]: 'No Notte',
  [DESIDERATA_TYPES.NO_REPERIBILITA_GIORNO]: 'NO RG (no reperibilità giorno)',
  [DESIDERATA_TYPES.NO_REPERIBILITA_NOTTE]: 'NO RN (no reperibilità notte)',
};

/** Opzioni desiderata mostrate in UI (escluso No Pomeriggio-Notte) */
export const DESIDERATA_OPTIONS_UI = [
  DESIDERATA_TYPES.NO_MATTINO,
  DESIDERATA_TYPES.NO_POMERIGGIO,
  DESIDERATA_TYPES.NO_NOTTE,
  DESIDERATA_TYPES.NO_REPERIBILITA_GIORNO,
  DESIDERATA_TYPES.NO_REPERIBILITA_NOTTE,
];

/** Lettere slot per abbreviazione desiderata: M=Mattino, P=Pomeriggio, N=Notte */
export const DESIDERATA_TO_SLOT_LETTERS = {
  [DESIDERATA_TYPES.NO_MATTINO]: ['M'],
  [DESIDERATA_TYPES.NO_POMERIGGIO]: ['P'],
  [DESIDERATA_TYPES.NO_POMERIGGIO_NOTTE]: ['P', 'N'],
  [DESIDERATA_TYPES.NO_NOTTE]: ['N'],
  [DESIDERATA_TYPES.NO_REPERIBILITA_GIORNO]: [],
  [DESIDERATA_TYPES.NO_REPERIBILITA_NOTTE]: [],
};

// Giorni della settimana
export const WEEKDAYS = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];
export const WEEKDAY_LABELS = {
  lun: 'Lunedì', mar: 'Martedì', mer: 'Mercoledì', gio: 'Giovedì',
  ven: 'Venerdì', sab: 'Sabato', dom: 'Domenica',
};
export const WEEKDAY_SHORT = {
  lun: 'Lun', mar: 'Mar', mer: 'Mer', gio: 'Gio',
  ven: 'Ven', sab: 'Sab', dom: 'Dom',
};

/** Giorni attivi da un template calendario (retrocompat. con campo singolo `weekday`). */
export function getTemplateWeekdays(t) {
  if (!t) return [];
  let w = t.weekdays;
  if (typeof w === 'string') {
    try {
      w = JSON.parse(w);
      if (typeof w === 'string') {
        try {
          w = JSON.parse(w);
        } catch {
          w = [];
        }
      }
    } catch {
      w = [];
    }
  }
  if (Array.isArray(w) && w.length > 0) return w.filter(x => typeof x === 'string' && x);
  return t.weekday ? [t.weekday] : [];
}

/** Ordina giorni nel ciclo lun–dom (stabilità colonna `weekday` di appoggio). */
export function sortWeekdaysCalendarOrder(wds) {
  const set = new Set(wds || []);
  return WEEKDAYS.filter(w => set.has(w));
}

// UOC/UOS - unità organizzative
export const UOC_IDS = {
  ANESTESIA_TI: 'uoc_anestesia_ti',
  BLOCCO_OPERATORIO: 'uos_blocco_operatorio',
  MEDICINA_DOLORE: 'uos_medicina_dolore',
};

export const UOC_LABELS = {
  [UOC_IDS.ANESTESIA_TI]: 'UOC Anestesia, Terapia Intensiva e Semi-intensiva',
  [UOC_IDS.BLOCCO_OPERATORIO]: 'UOS Programmazione e Attività Blocco Operatorio',
  [UOC_IDS.MEDICINA_DOLORE]: 'UOS Medicina del Dolore, Cure Palliative e Hospice',
};

export const CATEGORY_TO_UOC = Object.freeze({ ...categoryToUocJson });

/** Default nuovo medico: 5 × 20% */
export function defaultDistribuzioneFiveAxes() {
  const v = 20;
  return {
    [DISTRIBUTION_KEYS.TERAPIA_INTENSIVA]: v,
    [DISTRIBUTION_KEYS.FORMAZIONE]: v,
    [DISTRIBUTION_KEYS.SALA_OPERATORIA]: v,
    [DISTRIBUTION_KEYS.MEDICINA_DOLORE]: v,
    [DISTRIBUTION_KEYS.CURE_PALLIATIVE]: v,
  };
}

/** Classe CSS badge Piano turni / Calendario */
export function badgeClassForSlotCategory(category, slot = null) {
  if (slot && isSoMedicinaDoloreSlot(slot)) return 'md';
  switch (category) {
    case 'terapia_intensiva': return 'ti';
    case 'sala_operatoria': return 'so';
    case 'nora': return 'nora';
    case 'medicina_dolore': return 'md';
    case 'cure_palliative': return 'cp';
    case 'formazione': return 'formazione';
    case 'direzione': return 'dir';
    default: return 'dir';
  }
}

// Riferimento full-time: 38h/settimana → 7,6h/giorno. Part-time: impostare debito settimanale in anagrafica (÷5 = ore/giorno).
// Festivi infrasettimanali: un giorno feriale in meno nella settimana. Accrediti assenze: ancora calcolati sul riferimento 7,6h (congedo ecc.).
export const DEFAULT_WEEKLY_HOURS = 38;
export const HOURS_PER_DAY = 7.6;

// Tetto orario clinico per settimana ISO (allineato al solver, solver_core/infra_spread.py):
// il cap fisico assoluto è 42h, il tetto di ogni medico è il suo debito settimanale più il
// margine di straordinario (42 − 38 = 4h, lo stesso di cui gode oggi un full-time).
// Così 38h → 42h (invariato), 18h → 22h (max 3 slot da 6h), 0h → nessuno slot.
export const ISO_WEEK_MAX_CLINICAL_HOURS = 42;
export const ISO_WEEK_OVERTIME_HEADROOM_HOURS = ISO_WEEK_MAX_CLINICAL_HOURS - DEFAULT_WEEKLY_HOURS;

// Ore per slot (6h). I tempi extra fra consegne/ritardi compensano le 2h settimanali mancanti.
export const HOURS_PER_SLOT = 6;
/** Ore turno prolungato (8-15:30 = 7,5h) */
export const HOURS_PER_PROLUNGATO = 7.5;
/** Ore turno notte (20-8 = 12h effettive) */
export const HOURS_PER_NIGHT = 12;
/** Notte 20-8 ripartita sui due giorni di calendario: 20-24 = 4h sul giorno di inizio, 0-8 = 8h sul giorno successivo. */
export const HOURS_NIGHT_START_DAY = 4;
export const HOURS_NIGHT_NEXT_DAY = 8;
/** Ore per reperibilità (notte 20-8 e giorno 8-20 = 12h ciascuna) */
export const HOURS_PER_REPERIBILITA = 12;

/** Ore per fascia oraria (per notte, prolungato, reperibilità, o slot standard). Ogni reperibilità = 12h (blocco fisso). */
export function getHoursForSlot(timeSlot) {
  if (timeSlot === 'notte') return HOURS_PER_NIGHT;
  if (timeSlot === 'prolungato') return HOURS_PER_PROLUNGATO;
  if (timeSlot === 'reperibilita_notte' || timeSlot === 'reperibilita_giorno' || timeSlot === 'reperibilita') return HOURS_PER_REPERIBILITA;
  return HOURS_PER_SLOT;
}

/** Unità di conteggio slot per fascia: M/P=1, Pr=1,2, N=2. Reperibilità=0 (non entrano nel conteggio). Per slot variabili (es. formazione) lo slot può avere hoursOverride/slotWeightOverride. */
export function getSlotWeight(timeSlot) {
  if (!timeSlot) return 1;
  if (timeSlot === 'reperibilita_notte' || timeSlot === 'reperibilita_giorno' || timeSlot === 'reperibilita') return 0;
  if (timeSlot === 'notte') return 2;
  if (timeSlot === 'prolungato') return 1.2;
  return 1; // mattino, pomeriggio, altri
}
